import dom from '../utils/dom';


/** @typedef {import("./types").Address} Address */
/** @typedef {import("./types").AddressInputs} AddressInputs */
/** @typedef {import("./types").AddressInputSelectors} AddressInputSelectors */


const COUNTRY_ID_WHITELIST = [
  1, // USA
  32, // CAN
  94, // IRL
  149, // NZL
  209, // GBR
  218, // WAL
  227, // AUS
];

/** @type {AddressInputSelectors} */
const defaultAddressInputSelectors = {
  address1: '#Address_Address1',
  address2: '#Address_Address2',
  city: '#Address_City',
  state: '#Address_State',
  postalCode: '#Address_PostalCode',
  countryId: '#Address_CountryId',
};


/**
 * @param {HTMLFormElement} $form
 * @returns {HTMLInputElement[]}
 */
function getEmptyRequiredFormInputs($form) {
  const $emptyInputs = dom.findAllFrom($form, 'input[data-tn-required=true]')
    .filter($input => !$input.value.trim());

  const $emptySelects = dom.findAllFrom($form, 'select[data-tn-required=true]')
    .filter($select => $select.options.length > 0 && !$select.value.trim());

  return [...$emptyInputs, ...$emptySelects];
}


/**
 * @param {Address} address
 * @returns {boolean}
 */
function shouldVerifyAddress(address) {
  return COUNTRY_ID_WHITELIST.includes(parseInt(address.countryId, 10));
}


/**
 * @param {Address} address1
 * @param {Address} address2
 * @returns {boolean}
 */
function testAddressEquality(address1, address2) {
  if (address1 && address2) {
    return Object.entries(address1)
      .every(([key, value]) => address2[key] === value);
  }

  return false;
}


/**
 * @param {HTMLFormElement} $form
 * @param {AddressInputSelectors} inputSelectors
 * @returns {AddressInputs}
 */
function getAddressInputsFromForm($form, inputSelectors = defaultAddressInputSelectors) {
  return {
    address1: dom.findFrom($form, inputSelectors.address1),
    address2: dom.findFrom($form, inputSelectors.address2),
    city: dom.findFrom($form, inputSelectors.city),
    state: dom.findFrom($form, inputSelectors.state),
    postalCode: dom.findFrom($form, inputSelectors.postalCode),
    countryId: dom.findFrom($form, inputSelectors.countryId),
  };
}


/**
 * @param {AddressInputs} inputs
 * @returns {Address}
 */
function getAddressFromInputs(inputs) {
  const data = {};

  Object.entries(inputs)
    .forEach(([name, $el]) => {
      data[name] = $el.value;
    });

  return data;
}


/**
 * @param {HTMLFormElement} $form
 * @param {AddressInputSelectors} inputSelectors
 */
function getAddressFromForm($form, inputSelectors = defaultAddressInputSelectors) {
  const inputs = getAddressInputsFromForm($form, inputSelectors);

  return getAddressFromInputs(inputs);
}


/**
 * @param {AddressInputs} inputs
 * @param {Address} newAddress
 */
function updateAddressInputValues(inputs, newAddress) {
  Object.entries(inputs)
    .forEach(([name, $el]) => {
      $el.value = newAddress[name];
    });
}


/**
 * @param {HTMLFormElement} $form
 * @param {Address} newAddress
 * @param {AddressInputSelectors} inputSelectors
 */
function updateFormAddress($form, newAddress, inputSelectors = defaultAddressInputSelectors) {
  const inputs = getAddressInputsFromForm($form, inputSelectors);

  updateAddressInputValues(inputs, newAddress);
}


export default {
  getAddressInputsFromForm,
  updateFormAddress,
  updateAddressInputValues,
  getAddressFromForm,
  getAddressFromInputs,
  testAddressEquality,
  getEmptyRequiredFormInputs,
  shouldVerifyAddress,
  defaultAddressInputSelectors,
};



// WEBPACK FOOTER //
// ./V7/addressVerification/AddressVerificationHelpers.js